import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type { StackBillingBreakdown } from "@domains/application/types";
import { Application } from "src/domains/application/types";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import {
  formatCloudProvider,
  getCardStyles,
  getStatusChipProps,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

import { StackMetrics } from "../types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface DetectedService {
  name: string;
  type: string;
  port?: number;
  dockerfilePath?: string;
  healthEndpoint?: string;
  internetFacing: boolean;
}

interface EnvironmentInfo {
  slug: string;
  name: string;
}

interface AppResource {
  alias: string;
  resource: {
    alias: string;
    name: string;
    status: string;
    resourceType: string;
  };
}

interface DeploymentEventRow {
  systemAlias: string;
  step: string;
  status: string;
  severity: string;
  message: string;
  createdAt: string;
}

interface OverviewTabProps {
  system: Application;
  metrics: StackMetrics | null;
  metricsLoading: boolean;
  onBuildAndDeploy?: () => void;
  onTabChange?: (tab: string) => void;
  environments?: EnvironmentInfo[];
  onPromote?: (slug: string) => void;
  billingData?: StackBillingBreakdown | null;
  orgAlias?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDetectedServices(system: Application): DetectedService[] {
  const config = system.stackConfig as Record<string, unknown> | undefined;
  if (!config) return [];
  const services = config._detectedServices as DetectedService[] | undefined;
  if (Array.isArray(services)) return services;
  return [];
}

function isAdopted(system: Application): boolean {
  const config = system.stackConfig as Record<string, unknown> | undefined;
  return config?._adopted === true;
}

/** "12m ago" / "2h ago" / "1d ago" style relative time. */
function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function severityDotColor(
  severity: string,
): "success" | "error" | "warning" | "primary" {
  const s = (severity ?? "").toLowerCase();
  if (s === "error") return "error";
  if (s === "warning") return "warning";
  if (s === "info") return "primary";
  return "success";
}

/** Extract owner/repo from a GitHub URL. */
function repoShortName(url: string): string {
  const match = url.match(/github\.com[/:]([^/]+\/[^/]+?)(?:\.git)?$/);
  return match ? match[1] : url.replace(/^https?:\/\//, "");
}

// ── Main component ────────────────────────────────────────────────────────────

export function OverviewTab({
  system,
  metrics: _metrics,
  onBuildAndDeploy,
  onTabChange,
  environments = [],
  onPromote,
  billingData,
  orgAlias: _orgAlias,
}: OverviewTabProps) {
  const theme = useTheme();
  const detectedServices = getDetectedServices(system);
  const adopted = isAdopted(system);
  const cardSx = getCardStyles(theme);

  // ── Fetch resources + recent activity ──────────────────────────────────────

  const [resources, setResources] = useState<AppResource[]>([]);
  const [recentEvents, setRecentEvents] = useState<DeploymentEventRow[]>([]);

  const fetchOverviewData = useCallback(async () => {
    const token = orgSessionManager.getToken() ?? "";
    const headers = { Authorization: `Bearer ${token}` };

    // Best-effort fetch — failures don't block the Overview tab
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${encodeURIComponent(system.alias)}/resources`,
        { headers },
      );
      if (res.ok) setResources((await res.json()) as AppResource[]);
    } catch {
      // silent
    }

    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/deployment-events?limit=4`,
        { headers },
      );
      if (res.ok) {
        const data = (await res.json()) as { events: DeploymentEventRow[] };
        setRecentEvents(data.events ?? []);
      }
    } catch {
      // silent
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchOverviewData();
  }, [fetchOverviewData]);

  // ── Badge info ──────────────────────────────────────────────────────────────

  const isDeploying = ["deploying", "building", "provisioning"].includes(
    (system.status ?? "").toLowerCase(),
  );
  const isFailed = ["failed", "error", "provisioning_failed"].includes(
    (system.status ?? "").toLowerCase(),
  );
  const isRunning = ["running", "active"].includes(
    (system.status ?? "").toLowerCase(),
  );

  const mtdCost = billingData?.totalUsd ? `$${billingData.totalUsd}` : "$0.00";

  return (
    <Stack spacing={3}>
      {/* ── 2-column grid ── */}
      <Grid container spacing={2}>
        {/* Left column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* About card */}
            <Stack sx={cardSx} spacing={1.5}>
              <Typography sx={sectionLabelSx}>About</Typography>
              {/* Badge row */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Badge
                  icon={isFailed ? "✕" : "✓"}
                  label={isFailed ? "Build failing" : "Build passing"}
                  color={isFailed ? "error" : "success"}
                />
                <Badge
                  icon={isDeploying ? "⚡" : "✓"}
                  label={
                    isDeploying
                      ? "Deploy in progress"
                      : isRunning
                        ? "Deployed"
                        : "Not deployed"
                  }
                  color={
                    isDeploying ? "primary" : isRunning ? "success" : "default"
                  }
                />
                <Badge icon="$" label={`${mtdCost} MTD`} color="default" />
              </Stack>
              {/* Readme / description */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {system.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {system.description ||
                    (adopted
                      ? `Adopted from ${system.vcsRepoUrl?.replace(/^https?:\/\//, "") ?? "repository"}`
                      : "No description available.")}
                </Typography>
                {system.deployedAt && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Last deploy:</strong>{" "}
                    {relativeTime(system.deployedAt)} to{" "}
                    {system.environmentId ? "environment" : "dev"}
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Environments card */}
            <Stack sx={cardSx} spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={sectionLabelSx}>Environments</Typography>
                <Button
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                  onClick={() => onTabChange?.("configuration")}
                >
                  Manage
                </Button>
              </Stack>
              <Grid container spacing={1.5}>
                {environments.map((env) => {
                  const isCurrentEnv = system.environmentId != null;
                  const isDev = env.slug === "dev";
                  return (
                    <Grid item xs={12} sm={4} key={env.slug}>
                      <Stack
                        spacing={1}
                        sx={{
                          p: 1.5,
                          border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                          borderRadius: 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {env.name}
                          </Typography>
                          <Chip
                            label={
                              isCurrentEnv && isDev ? "Running" : "Not deployed"
                            }
                            size="small"
                            color={
                              isCurrentEnv && isDev ? "success" : "default"
                            }
                            variant={
                              isCurrentEnv && isDev ? "filled" : "outlined"
                            }
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {isCurrentEnv && isDev
                            ? `Last deploy: ${relativeTime(system.deployedAt ?? null)}`
                            : "Ready to promote"}
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          sx={{
                            textTransform: "none",
                            fontSize: 12,
                            justifyContent: "flex-start",
                            p: 0,
                          }}
                          onClick={() => onPromote?.(env.slug)}
                        >
                          {isDev ? "View logs →" : "Promote →"}
                        </Button>
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          </Stack>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* Details card */}
            <Stack sx={cardSx} spacing={0}>
              <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
                Details
              </Typography>
              <MetaRow
                label="Source"
                value={
                  system.vcsRepoUrl ? (
                    <Link
                      href={system.vcsRepoUrl}
                      target="_blank"
                      rel="noopener"
                      sx={{ textDecoration: "none", color: "primary.main" }}
                    >
                      {repoShortName(system.vcsRepoUrl)}
                    </Link>
                  ) : (
                    "—"
                  )
                }
              />
              <MetaRow
                label="Deploy mode"
                value={
                  system.deployMode === "ci_managed"
                    ? "CI managed"
                    : system.deployMode === "ci_build_rusha_deploy"
                      ? "CI Build → Rusha"
                      : "Rusha managed"
                }
              />
              <MetaRow label="Framework" value={system.systemType ?? "—"} />
              <MetaRow
                label="Cloud provider"
                value={formatCloudProvider(system.cloudProvider)}
              />
              <MetaRow
                label="Created"
                value={new Date(system.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
                last
              />
            </Stack>

            {/* Resources card */}
            <Stack sx={cardSx} spacing={0}>
              <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
                Resources
              </Typography>
              {resources.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  No resources attached to this stack.
                </Typography>
              ) : (
                resources.map((r, i) => {
                  const chip = getStatusChipProps(r.resource.status);
                  return (
                    <MetaRow
                      key={r.resource.alias}
                      label={
                        <Link
                          href="#"
                          sx={{
                            textDecoration: "none",
                            color: "text.primary",
                            fontWeight: 500,
                          }}
                        >
                          {r.resource.name}
                        </Link>
                      }
                      value={
                        <Chip
                          label={chip.label}
                          size="small"
                          color={chip.color}
                          variant={chip.variant}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      }
                      last={i === resources.length - 1}
                    />
                  );
                })
              )}
            </Stack>

            {/* Latest Activity card */}
            <Stack sx={cardSx} spacing={0}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography sx={sectionLabelSx}>Latest Activity</Typography>
                <Button
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                  onClick={() => onTabChange?.("events")}
                >
                  View all
                </Button>
              </Stack>
              {recentEvents.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  No recent activity.
                </Typography>
              ) : (
                recentEvents.slice(0, 4).map((ev, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                    sx={{
                      py: 1.25,
                      borderBottom:
                        i < Math.min(recentEvents.length, 4) - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                          : "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor:
                          theme.palette[severityDotColor(ev.severity)].main,
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Stack spacing={0.25}>
                      <Typography variant="body2">{ev.message}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {relativeTime(ev.createdAt)}
                      </Typography>
                    </Stack>
                  </Stack>
                ))
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      {/* ── Detected Services card (below the 2-column grid) ── */}
      <Stack sx={cardSx} spacing={1.5}>
        <Typography sx={sectionLabelSx}>
          Detected Services ({detectedServices.length})
        </Typography>
        {detectedServices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No services detected.{" "}
            {adopted
              ? "Services will appear here after the adoption analysis."
              : "Configure your stack to add services."}
          </Typography>
        ) : (
          detectedServices.map((svc) => (
            <Box
              key={svc.name}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5,
                px: 2,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {svc.name.charAt(0).toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="body2" fontWeight={600}>
                  {svc.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {svc.type}
                  {svc.port ? ` · :${svc.port}` : ""}
                  {svc.dockerfilePath ? ` · ${svc.dockerfilePath}` : ""}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <PipelinePills />
              </Box>
              <Stack direction="row" spacing={1}>
                {svc.internetFacing && (
                  <Chip
                    label="Internet-facing"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      color: theme.palette.success.main,
                      border: "none",
                    }}
                  />
                )}
                <Chip
                  label="500m / 512Mi"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    bgcolor: alpha(theme.palette.text.secondary, 0.08),
                    color: "text.secondary",
                    border: "none",
                  }}
                />
              </Stack>
            </Box>
          ))
        )}
      </Stack>

      {/* ── Quick Actions row ── */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}
      >
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          QUICK ACTIONS
        </Typography>
        <Button
          variant="contained"
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none" }}
          onClick={onBuildAndDeploy}
        >
          ▶ Run Pipeline
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none" }}
          onClick={() => onTabChange?.("configuration")}
        >
          Settings
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none" }}
          onClick={() => onTabChange?.("manifest")}
        >
          Manifest
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none" }}
          onClick={() => onTabChange?.("costs")}
        >
          Costs
        </Button>
      </Stack>
    </Stack>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetaRow({
  label,
  value,
  last,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  last?: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 1.25,
        borderBottom: last ? "none" : `1px solid ${alpha("#94a3b8", 0.08)}`,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Stack>
  );
}

function Badge({
  icon,
  label,
  color,
}: {
  icon: string;
  label: string;
  color: "success" | "error" | "warning" | "primary" | "default";
}) {
  const theme = useTheme();
  const colors: Record<string, { bg: string; text: string }> = {
    success: {
      bg: alpha(theme.palette.success.main, 0.08),
      text: theme.palette.success.main,
    },
    error: {
      bg: alpha(theme.palette.error.main, 0.08),
      text: theme.palette.error.main,
    },
    warning: {
      bg: alpha(theme.palette.warning.main, 0.08),
      text: theme.palette.warning.main,
    },
    primary: {
      bg: alpha(theme.palette.primary.main, 0.08),
      text: theme.palette.primary.main,
    },
    default: {
      bg: alpha(theme.palette.text.secondary, 0.08),
      text: theme.palette.text.secondary,
    },
  };
  const c = colors[color] ?? colors.default;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: 1,
        bgcolor: c.bg,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.text }}>
        {icon}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: c.text }}>
        {label}
      </Typography>
    </Stack>
  );
}

function PipelinePills() {
  const pills = ["Build", "Deploy", "Running"];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {pills.map((pill, i) => (
        <React.Fragment key={pill}>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: "rgba(148,163,184,0.08)",
              fontSize: 11,
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            {pill}
          </Box>
          {i < pills.length - 1 && (
            <Typography sx={{ color: "text.disabled", fontSize: 14 }}>
              →
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

export default OverviewTab;
